/**
 * DepthSegmentationCore
 *
 * Pure depth-band segmentation pipeline with no DOM or THREE.js dependencies.
 * Can be used in workers and on the main thread.
 *
 * This pipeline processes a position map (RGBA image data) and produces:
 * - Connected component regions based on depth bands
 * - Region adjacency graph with connectivity information
 * - Auto-prompts for each region (centroids + convex hulls)
 */

import { BasePipeline } from '../BasePipeline';

// ─── Types ─────────────────────────────────────────────────────────────────

export interface RegionGraphEdge {
  targetRegionId: number;
  sharedPixels: number;
  connectivityPercent: number;
}

export interface RegionGraphNode {
  regionId: number;
  centroid: { x: number; y: number };
  interiorPoint: { x: number; y: number };
  centroidNormalized: { x: number; y: number };
  interiorPointNormalized: { x: number; y: number };
  size: number;
  band: number;
  touchesEdge: boolean;
  edges: RegionGraphEdge[];
  perimeterPixels: number;
  selected: boolean;
}

export interface RegionGraph {
  nodes: Map<number, RegionGraphNode>;
  minSize: number;
}

export interface DepthAutoPrompt {
  x: number; // normalized [0, 1]
  y: number; // normalized [0, 1]
  label: number; // 1 = positive, 0 = negative
  hull: Array<{ px: number; py: number }>;
  imageWidth: number;
  imageHeight: number;
}

/**
 * Input to the depth segmentation pipeline
 */
export interface DepthSegmentationInput {
  data: Uint8ClampedArray;
  width: number;
  height: number;
  /**
   * Optional x-ray (density) capture of the same view, RGBA white-on-black.
   * Used by `remove_invalid_areas` to confirm a mid-depth plateau pixel is
   * backed by rendered structure before punching it.
   */
  xray?: { data: Uint8ClampedArray; width: number; height: number };
  /**
   * Polygons in normalized [0,1] image space that are already covered by
   * committed annotations (existing rooms). `remove_invalid_areas` punches
   * every pixel inside them so they can't form regions again.
   */
  excludePolygons?: Array<Array<{ x: number; y: number }>>;
}

/**
 * Context passed between pipeline passes
 */
export interface DepthSegmentationContext {
  // Input
  width: number;
  height: number;
  src: Uint8ClampedArray;
  xray?: { data: Uint8ClampedArray; width: number; height: number };
  excludePolygons?: Array<Array<{ x: number; y: number }>>;

  // After grayscale pass
  gray?: Uint8Array;
  alpha?: Uint8Array;

  // After band_assignment pass
  pixelBand?: Int32Array;

  // After pre_cc_noise pass: 1 for pixels that were band-assigned but punched
  // to band=-1 by the chopper. The post-CC heal pass reassigns these to
  // their majority-neighbor region so the chopper leaves no visual scar.
  noisePunched?: Uint8Array;

  // After remove_invalid_areas pass: 1 for pixels punched by the mid-depth
  // plateau rule. NOT set for exclude-polygon punches — committed rooms stay
  // removed. The final reclaim_invalid_areas pass hands these holes to their
  // closest region so they can't leave gaps, while still being absent during
  // band assignment / connected components (which is what stops snaking).
  invalidPunched?: Uint8Array;

  // After connected_components pass
  componentId?: Int32Array;
  regionSizes?: Map<number, number>;
  regionBands?: Map<number, number>;
  regionCentroids?: Map<number, { sumX: number; sumY: number }>;
  regionSamplePixel?: number[];
  edgeTouchingRegions?: Set<number>;
  minRegionPixels?: number;

  // After colored_data pass
  coloredData?: Uint8ClampedArray;
  regionColors?: Map<number, number>;
  regionPixels?: Map<number, number[]>;

  // After region_graph pass
  regionGraph?: RegionGraph;
  showRegionGraph?: boolean;

  // After auto_prompts pass
  autoPrompts?: DepthAutoPrompt[];
}

/**
 * Output from the depth segmentation pipeline
 */
export interface DepthSegmentationOutput {
  width: number;
  height: number;
  componentId: Int32Array;
  regionSizes: Map<number, number>;
  regionBands: Map<number, number>;
  regionCentroids: Map<number, { sumX: number; sumY: number }>;
  regionSamplePixel: number[];
  edgeTouchingRegions: Set<number>;
  minRegionPixels: number;
  coloredData: Uint8ClampedArray;
  regionColors: Map<number, number>;
  regionPixels: Map<number, number[]>;
  regionGraph: RegionGraph | null;
  showRegionGraph: boolean;
  autoPrompts: DepthAutoPrompt[];
}

/**
 * Kwargs for pipeline passes
 */
export interface DepthSegmentationKwargs {
  bandCount?: number;
  minRegionPixelsPercent?: number;
  minRegionPixelsAbsolute?: number;
  /** Skip bands below this value (0 = keep all, 1 = ignore band 0, etc.) */
  ignoreBandsBelow?: number;
  topBandPercent?: number;
  showRegionGraph?: boolean;
  regionGraphMinSize?: number;
  fillSmallGapsMaxPixels?: number;
  mergeSmallRegionsMaxPixels?: number;
  /**
   * Maximum height difference for `merge_small_regions`, in MIRRORED
   * grayscale levels (`ctx.gray`, the post-LUT depth the banding passes see —
   * see `grayscalePass`). A small region only merges into a neighbor whose
   * mean gray is within this delta, so slivers don't get absorbed by a
   * surface in a different depth band just because they touch it. Note the
   * mirror is non-monotonic in TRUE height: two surfaces on opposite sides
   * of mid-Z can share similar gray values — the gate matches what band
   * assignment sees, not physical elevation. Omit / 0 disables the gate
   * (any best-connected neighbor qualifies, the previous behavior).
   */
  mergeSmallRegionsMaxHeightDelta?: number;
  /**
   * Maximum size (pixels) of an enclosed-hole region eligible for filling by
   * `fill_enclosed_holes`. Omit / 0 disables the size cap (any enclosed
   * region merges into its dominant neighbor).
   */
  fillEnclosedHolesMaxPixels?: number;
  /** Compactness threshold below which a region is considered snaking (0-1, default 0.15) */
  splitSnakingCompactnessThreshold?: number;
  /** Erosion radius in pixels for splitting snaking regions (default 3) */
  splitSnakingErosionRadius?: number;
  /** Minimum region size in pixels to consider for snaking split (default 200) */
  splitSnakingMinRegionSize?: number;
  /**
   * Number of pixels to grow rendered content into transparent pixels (alpha == 0),
   * closing small holes before band assignment. 0 disables the pass.
   */
  growTransparentPixels?: number;
  /**
   * Number of pixels to erode back after growing. Counts only dilated pixels —
   * originally-rendered pixels are never removed. Set lower than
   * `growTransparentPixels` for an asymmetric close that bridges isolated
   * clusters; equal for a symmetric close that returns to the original
   * silhouette. 0 keeps all dilated pixels.
   */
  shrinkTransparentPixels?: number;
  /**
   * Tolerance (± gray levels) around the mid-depth value 127 for the
   * `remove_invalid_areas` pass. Pixels whose grayscale value falls within
   * [127 - tolerance, 127 + tolerance] are punched to black (gray=0, alpha=0).
   * 0 (default) matches gray=127 exactly.
   */
  removeGrayTolerance?: number;
  /**
   * Minimum x-ray whiteness (percent of full white, 0..100) required before
   * `remove_invalid_areas` punches a mid-depth plateau pixel. Only applies
   * when an x-ray capture was provided on the input: a plateau pixel whose
   * x-ray luminance is below this is left alone — there's no rendered
   * structure backing it, so punching would only tear holes. Default 0.7
   * (i.e. any faintly rendered x-ray content qualifies).
   */
  removeInvalidXrayMinWhitePercent?: number;

  /**
   * Fraction (0..1) of band-assigned pixels to punch to band=-1 before
   * connected-components. Acts as a stochastic edge-detector: ribbons
   * (long thin regions) are fragile — losing a small percentage of pixels
   * disconnects them. Rooms (large compact regions) are robust — many
   * parallel paths route around the punched pixels. The downstream
   * `heal_chopper_lines` pass reassigns every punched pixel to its largest
   * neighboring region, so the noise leaves no visual scar.
   *
   * The noise source is the raw position map's A byte (the low bits of XY
   * eye-position), which is itself essentially per-pixel noise — no PRNG
   * needed. A pixel is punched when `A < threshold`, where `threshold =
   * round(fraction * 256)`. 0 disables.
   *
   * Recovers the cleaner segmentation that the pipeline produced before the
   * grayscale LUT-clamp and B-only fixes — those fixes (correctly) stopped
   * the alpha-channel from accidentally punching pixels, but that punching
   * was doing real work as a ribbon chopper.
   */
  preCCNoiseFraction?: number;
}

// ─── Pipeline Implementation ───────────────────────────────────────────────

export class DepthSegmentationPipeline extends BasePipeline<DepthSegmentationInput, DepthSegmentationOutput, DepthSegmentationContext, DepthSegmentationKwargs> {
  // Pre-computed lookup table for grayscale conversion (avoids branches in hot loop).
  // Mirror maps B around 128: b < 128 → 255 - b*2, b >= 128 → (b - 128) * 2.
  // The raw mirror would produce gray=0 at b=128, which collides with the
  // unrendered sentinel and punches holes through any surface that lands near
  // mid-Z. Clamp the minimum gray to 1 so every rendered pixel is bandable.
  private static grayscaleLUT: Uint8Array = (() => {
    const lut = new Uint8Array(256);
    for (let b = 0; b < 256; b++) {
      const mirrored = b < 128 ? 255 - b * 2 : (b - 128) * 2;
      lut[b] = mirrored === 0 ? 1 : mirrored;
    }
    return lut;
  })();

  constructor() {
    super();
    this.initializeProcessorRegistry();
    this.initializePipeline();
  }

  protected inputToContext(input: DepthSegmentationInput): DepthSegmentationContext {
    return {
      width: input.width,
      height: input.height,
      src: input.data,
      xray: input.xray,
      excludePolygons: input.excludePolygons,
    };
  }

  protected contextToOutput(ctx: DepthSegmentationContext): DepthSegmentationOutput {
    return {
      width: ctx.width,
      height: ctx.height,
      componentId: ctx.componentId ?? new Int32Array(0),
      regionSizes: ctx.regionSizes ?? new Map(),
      regionBands: ctx.regionBands ?? new Map(),
      regionCentroids: ctx.regionCentroids ?? new Map(),
      regionSamplePixel: ctx.regionSamplePixel ?? [],
      edgeTouchingRegions: ctx.edgeTouchingRegions ?? new Set(),
      minRegionPixels: ctx.minRegionPixels ?? 0,
      coloredData: ctx.coloredData ?? new Uint8ClampedArray(0),
      regionColors: ctx.regionColors ?? new Map(),
      regionPixels: ctx.regionPixels ?? new Map(),
      regionGraph: ctx.regionGraph ?? null,
      showRegionGraph: ctx.showRegionGraph ?? false,
      autoPrompts: ctx.autoPrompts ?? [],
    };
  }

  protected logPassSkipped(_name: string): void {
    // Silent in worker context
  }

  protected logPassFailed(name: string, error: unknown): void {
    console.warn(`[DepthSegmentation] Pass failed: ${name}`, error);
  }

  protected logPassStart(_name: string): void {
    // Silent
  }

  protected logPassComplete(_name: string, _elapsedTime: number): void {
    // Silent
  }

  private initializeProcessorRegistry(): void {
    // Note: processor IDs match the config file (depth-band-pipeline-config.json)
    this.registerProcessor('grayscale', this.grayscalePass.bind(this), {
      defaultKwargs: {},
    });

    this.registerProcessor('remove_invalid_areas', this.removeInvalidAreasPass.bind(this), {
      defaultKwargs: { removeGrayTolerance: 0, removeInvalidXrayMinWhitePercent: 0.7 },
    });

    // Legacy alias — saved configs may still reference the old `remove_gray` id.
    this.registerProcessor('remove_gray', this.removeInvalidAreasPass.bind(this), {
      defaultKwargs: { removeGrayTolerance: 0, removeInvalidXrayMinWhitePercent: 0.7 },
    });

    this.registerProcessor('grow_transparent', this.growTransparentPass.bind(this), {
      defaultKwargs: { growTransparentPixels: 0, shrinkTransparentPixels: 0 },
    });

    this.registerProcessor('band_assignment', this.bandAssignmentPass.bind(this), {
      defaultKwargs: { bandCount: 16 },
    });

    this.registerProcessor('pre_cc_noise', this.preCCNoisePass.bind(this), {
      defaultKwargs: { preCCNoiseFraction: 0.04 },
    });

    this.registerProcessor('connected_components_bfs', this.connectedComponentsPass.bind(this), {
      defaultKwargs: {
        minRegionPixelsPercent: 0.005,
        minRegionPixelsAbsolute: 50,
        ignoreBandsBelow: 0,
      },
    });

    this.registerProcessor('fill_small_gaps', this.fillSmallGapsPass.bind(this), {
      defaultKwargs: { fillSmallGapsMaxPixels: 20 },
    });

    this.registerProcessor('heal_chopper_lines', this.healChopperLinesPass.bind(this), {
      defaultKwargs: {},
    });

    this.registerProcessor('split_snaking_regions', this.splitSnakingRegionsPass.bind(this), {
      defaultKwargs: {
        splitSnakingCompactnessThreshold: 0.15,
        splitSnakingErosionRadius: 3,
        splitSnakingMinRegionSize: 200,
      },
    });

    this.registerProcessor('reclaim_invalid_areas', this.reclaimInvalidAreasPass.bind(this), {
      defaultKwargs: {},
    });

    this.registerProcessor('output_canvas', this.coloredDataPass.bind(this), {
      defaultKwargs: {},
    });

    this.registerProcessor('region_graph', this.regionGraphPass.bind(this), {
      defaultKwargs: { showRegionGraph: false, regionGraphMinSize: 500 },
    });

    this.registerProcessor('merge_small_regions', this.mergeSmallRegionsPass.bind(this), {
      defaultKwargs: { mergeSmallRegionsMaxPixels: 100, mergeSmallRegionsMaxHeightDelta: 0 },
    });

    this.registerProcessor('fill_enclosed_holes', this.fillEnclosedHolesPass.bind(this), {
      defaultKwargs: { fillEnclosedHolesMaxPixels: 0 },
    });

    this.registerProcessor('auto_prompts', this.autoPromptsPass.bind(this), {
      defaultKwargs: { topBandPercent: 0.5 },
    });
  }

  private initializePipeline(): void {
    this.addPass('Grayscale', this.grayscalePass.bind(this), {
      processorId: 'grayscale',
      kwargs: this.getDefaultKwargsForProcessor('grayscale'),
    })
      .addPass('Remove Invalid Areas', this.removeInvalidAreasPass.bind(this), {
        processorId: 'remove_invalid_areas',
        kwargs: this.getDefaultKwargsForProcessor('remove_invalid_areas'),
      })
      .addPass('Grow Transparent', this.growTransparentPass.bind(this), {
        processorId: 'grow_transparent',
        kwargs: this.getDefaultKwargsForProcessor('grow_transparent'),
      })
      .addPass('Band Assignment', this.bandAssignmentPass.bind(this), {
        processorId: 'band_assignment',
        kwargs: this.getDefaultKwargsForProcessor('band_assignment'),
      })
      .addPass('Pre-CC Noise', this.preCCNoisePass.bind(this), {
        processorId: 'pre_cc_noise',
        kwargs: this.getDefaultKwargsForProcessor('pre_cc_noise'),
      })
      .addPass('Connected Components BFS', this.connectedComponentsPass.bind(this), {
        processorId: 'connected_components_bfs',
        kwargs: this.getDefaultKwargsForProcessor('connected_components_bfs'),
      })
      .addPass('Fill Small Gaps', this.fillSmallGapsPass.bind(this), {
        processorId: 'fill_small_gaps',
        kwargs: this.getDefaultKwargsForProcessor('fill_small_gaps'),
      })
      .addPass('Heal Chopper Lines', this.healChopperLinesPass.bind(this), {
        processorId: 'heal_chopper_lines',
        kwargs: this.getDefaultKwargsForProcessor('heal_chopper_lines'),
      })
      .addPass('Fill Enclosed Holes', this.fillEnclosedHolesPass.bind(this), {
        processorId: 'fill_enclosed_holes',
        kwargs: this.getDefaultKwargsForProcessor('fill_enclosed_holes'),
      })
      .addPass('Split Snaking Regions', this.splitSnakingRegionsPass.bind(this), {
        processorId: 'split_snaking_regions',
        kwargs: this.getDefaultKwargsForProcessor('split_snaking_regions'),
      })
      .addPass('Output Canvas', this.coloredDataPass.bind(this), {
        processorId: 'output_canvas',
        kwargs: this.getDefaultKwargsForProcessor('output_canvas'),
      })
      .addPass('Reclaim Invalid Areas', this.reclaimInvalidAreasPass.bind(this), {
        processorId: 'reclaim_invalid_areas',
        kwargs: this.getDefaultKwargsForProcessor('reclaim_invalid_areas'),
      })
      .addPass('Region Graph', this.regionGraphPass.bind(this), {
        processorId: 'region_graph',
        kwargs: this.getDefaultKwargsForProcessor('region_graph'),
      })
      .addPass('Merge Small Regions', this.mergeSmallRegionsPass.bind(this), {
        processorId: 'merge_small_regions',
        kwargs: this.getDefaultKwargsForProcessor('merge_small_regions'),
      })
      .addPass('Auto Prompts', this.autoPromptsPass.bind(this), {
        processorId: 'auto_prompts',
        kwargs: this.getDefaultKwargsForProcessor('auto_prompts'),
      });
  }

  // ─── Pass Implementations ──────────────────────────────────────────────────

  /**
   * Build a mirrored grayscale image from the position map's B channel.
   *
   * Only B carries depth — R, G, and A are packed XY position used by the
   * alignment tools and must not influence segmentation. The clear color is
   * (0,0,0,0), so B===0 is the unrendered sentinel; `ctx.alpha` is synthesized
   * as 255/0 from that check and is the *only* unrendered signal downstream
   * passes should read. (Don't gate on `gray===0`: the LUT clamps every
   * rendered pixel to gray≥1, but doing so used to punch holes wherever a
   * surface landed near mid-Z and B's mirror produced gray=0.)
   */
  private grayscalePass(ctx: DepthSegmentationContext): DepthSegmentationContext {
    const src = ctx.src;
    const total = ctx.width * ctx.height;
    const gray = new Uint8Array(total);
    const alpha = new Uint8Array(total);
    const lut = DepthSegmentationPipeline.grayscaleLUT;

    const total4 = total & ~3;
    let pi = 0;
    let i = 0;

    for (; i < total4; i += 4, pi += 16) {
      const b0 = src[pi + 2];
      const b1 = src[pi + 6];
      const b2 = src[pi + 10];
      const b3 = src[pi + 14];

      gray[i] = b0 ? lut[b0] : 0;
      gray[i + 1] = b1 ? lut[b1] : 0;
      gray[i + 2] = b2 ? lut[b2] : 0;
      gray[i + 3] = b3 ? lut[b3] : 0;

      alpha[i] = b0 ? 255 : 0;
      alpha[i + 1] = b1 ? 255 : 0;
      alpha[i + 2] = b2 ? 255 : 0;
      alpha[i + 3] = b3 ? 255 : 0;
    }

    for (; i < total; i++, pi += 4) {
      const b = src[pi + 2];
      gray[i] = b ? lut[b] : 0;
      alpha[i] = b ? 255 : 0;
    }

    ctx.gray = gray;
    ctx.alpha = alpha;
    return ctx;
  }

  /**
   * Remove invalid areas — pixels that must not participate in band
   * assignment or connected-components:
   *
   * 1. **Existing annotation polygons** (`ctx.excludePolygons`, normalized
   *    [0,1] image space): areas already covered by a committed room are
   *    punched unconditionally so they can't be offered as regions again.
   *
   * 2. **The mid-depth plateau** — source pixels whose B channel is 127
   *    (± `removeGrayTolerance`). IMPORTANT: depth is carried by the *source*
   *    B channel, and the grayscale LUT mirrors it (b=127 → gray=1, not 127).
   *    So this pass reads `ctx.src` directly rather than the post-LUT `gray`
   *    buffer; matching gray===127 would hit a completely different depth.
   *    Mid-Z is the "no usable surface" plateau. When an x-ray capture is
   *    available (`ctx.xray`), a plateau pixel is only punched if the x-ray
   *    also shows rendered structure there (luminance ≥
   *    `removeInvalidXrayMinWhitePercent` % of full white) — a dark x-ray
   *    pixel means nothing was rendered behind the plateau value, and
   *    punching it would only tear extra holes.
   *
   * Setting gray=0 / alpha=0 reuses the unrendered sentinel the rest of the
   * pipeline already honours, so downstream passes skip these pixels
   * naturally.
   *
   * Order-proof: it stamps gray=0/alpha=0. If band assignment has already run
   * (pixelBand exists — i.e. this pass is placed AFTER Band Assignment), it
   * also stamps pixelBand=-1, because connected components and every
   * downstream pass key off pixelBand and never re-read alpha. Without that,
   * placing this pass after Band Assignment would have no visible effect.
   */
  private removeInvalidAreasPass(ctx: DepthSegmentationContext, kwargs?: DepthSegmentationKwargs): DepthSegmentationContext {
    const { src, gray, alpha, pixelBand, width, height, xray, excludePolygons } = ctx;
    if (!gray || !alpha) return ctx;

    const tolerance = kwargs?.removeGrayTolerance ?? 0;
    const lo = 127 - tolerance;
    const hi = 127 + tolerance;
    const minWhitePercent = kwargs?.removeInvalidXrayMinWhitePercent ?? 0.7;
    // The x-ray capture is a white-on-black grayscale render, so R carries
    // the full luminance.
    const xrayMinValue = (minWhitePercent / 100) * 255;
    const xraySameSize = xray !== undefined && xray.width === width && xray.height === height;
    const total = width * height;

    const excludeMask = excludePolygons && excludePolygons.length > 0 ? DepthSegmentationPipeline.rasterizePolygonMask(excludePolygons, width, height) : null;

    // Plateau punches are reclaimable at the end of the pipeline (see
    // reclaimInvalidAreasPass); exclude-polygon punches are not.
    const invalidPunched = ctx.invalidPunched ?? new Uint8Array(total);

    for (let i = 0; i < total; i++) {
      let punch = false;
      let reclaimable = false;

      if (excludeMask && excludeMask[i] !== 0) {
        punch = true;
      } else {
        const b = src[i * 4 + 2];
        if (b >= lo && b <= hi) {
          if (xray) {
            let lum: number;
            if (xraySameSize) {
              lum = xray.data[i * 4];
            } else {
              // Nearest-neighbor sample when the x-ray resolution differs.
              const x = i % width;
              const y = (i - x) / width;
              const sx = Math.min(xray.width - 1, ((x * xray.width) / width) | 0);
              const sy = Math.min(xray.height - 1, ((y * xray.height) / height) | 0);
              lum = xray.data[(sy * xray.width + sx) * 4];
            }
            punch = lum >= xrayMinValue;
          } else {
            punch = true;
          }
          reclaimable = punch;
        }
      }

      if (punch) {
        gray[i] = 0;
        alpha[i] = 0;
        if (pixelBand) pixelBand[i] = -1;
        if (reclaimable) invalidPunched[i] = 1;
      }
    }

    return { ...ctx, invalidPunched };
  }

  /**
   * Final reclaim of the invalid-area holes.
   *
   * The mid-depth plateau pixels punched by `remove_invalid_areas` exist so
   * regions can't snake through invalid space during band assignment and
   * connected components — but keeping the holes to the very end leaves
   * gaps inside selections. This pass hands every reclaimable hole pixel to
   * its CLOSEST region (multi-source BFS from the hole borders), updating
   * sizes, centroids, pixel lists and the colored canvas when present.
   * Exclude-polygon punches (committed rooms) are not marked reclaimable
   * and stay removed.
   *
   * Place it BEFORE the final `region_graph` pass so the graph's adjacency
   * reflects the reclaimed area.
   */
  private reclaimInvalidAreasPass(ctx: DepthSegmentationContext): DepthSegmentationContext {
    const { componentId, invalidPunched, regionSizes, regionCentroids, regionPixels, coloredData, edgeTouchingRegions, width, height } = ctx;
    if (!componentId || !invalidPunched) return ctx;
    const total = width * height;

    const queue = new Int32Array(total);
    let head = 0;
    let tail = 0;

    // Seed with labeled pixels bordering a reclaimable hole.
    for (let i = 0; i < total; i++) {
      if (componentId[i] === 0) continue;
      const x = i % width;
      if (
        (x > 0 && componentId[i - 1] === 0 && invalidPunched[i - 1] !== 0) ||
        (x < width - 1 && componentId[i + 1] === 0 && invalidPunched[i + 1] !== 0) ||
        (i >= width && componentId[i - width] === 0 && invalidPunched[i - width] !== 0) ||
        (i < total - width && componentId[i + width] === 0 && invalidPunched[i + width] !== 0)
      ) {
        queue[tail++] = i;
      }
    }

    const claim = (pixel: number, regId: number): void => {
      componentId[pixel] = regId;
      queue[tail++] = pixel;
      const x = pixel % width;
      const y = (pixel - x) / width;
      if (regionSizes) regionSizes.set(regId, (regionSizes.get(regId) ?? 0) + 1);
      const centroid = regionCentroids?.get(regId);
      if (centroid) {
        centroid.sumX += x;
        centroid.sumY += y;
      }
      regionPixels?.get(regId)?.push(pixel);
      if (coloredData) {
        const pi = pixel * 4;
        coloredData[pi] = regId & 0xff;
        coloredData[pi + 1] = (regId >> 8) & 0xff;
        coloredData[pi + 2] = (regId >> 16) & 0xff;
        coloredData[pi + 3] = 255;
      }
      if (edgeTouchingRegions && (x === 0 || x === width - 1 || y === 0 || y === height - 1)) {
        edgeTouchingRegions.add(regId);
      }
    };

    // BFS wavefront: each hole pixel joins the region of its nearest labeled
    // neighbor, so whole holes are swallowed regardless of depth.
    while (head < tail) {
      const p = queue[head++];
      const regId = componentId[p];
      const x = p % width;
      if (x > 0 && componentId[p - 1] === 0 && invalidPunched[p - 1] !== 0) claim(p - 1, regId);
      if (x < width - 1 && componentId[p + 1] === 0 && invalidPunched[p + 1] !== 0) claim(p + 1, regId);
      if (p >= width && componentId[p - width] === 0 && invalidPunched[p - width] !== 0) claim(p - width, regId);
      if (p < total - width && componentId[p + width] === 0 && invalidPunched[p + width] !== 0) claim(p + width, regId);
    }

    return ctx;
  }

  /**
   * Rasterize normalized [0,1] polygons into a per-pixel mask (1 = inside)
   * using an even-odd scanline fill sampled at pixel centers. Public: the
   * Wall Tracer reuses it to punch committed rooms out of its capture.
   */
  public static rasterizePolygonMask(polygons: Array<Array<{ x: number; y: number }>>, width: number, height: number): Uint8Array {
    const mask = new Uint8Array(width * height);
    const xIntersections: number[] = [];

    for (const polygon of polygons) {
      const n = polygon.length;
      if (n < 3) continue;

      let minY = Infinity;
      let maxY = -Infinity;
      const xs = new Float64Array(n);
      const ys = new Float64Array(n);
      for (let i = 0; i < n; i++) {
        xs[i] = polygon[i].x * width;
        ys[i] = polygon[i].y * height;
        if (ys[i] < minY) minY = ys[i];
        if (ys[i] > maxY) maxY = ys[i];
      }

      const y0 = Math.max(0, Math.floor(minY));
      const y1 = Math.min(height - 1, Math.ceil(maxY));

      for (let y = y0; y <= y1; y++) {
        const yCenter = y + 0.5;
        xIntersections.length = 0;
        for (let i = 0, j = n - 1; i < n; j = i++) {
          const yi = ys[i];
          const yj = ys[j];
          if ((yi <= yCenter && yj > yCenter) || (yj <= yCenter && yi > yCenter)) {
            xIntersections.push(xs[i] + ((yCenter - yi) / (yj - yi)) * (xs[j] - xs[i]));
          }
        }
        xIntersections.sort((a, b) => a - b);
        for (let k = 0; k + 1 < xIntersections.length; k += 2) {
          const xStart = Math.max(0, Math.ceil(xIntersections[k] - 0.5));
          const xEnd = Math.min(width - 1, Math.floor(xIntersections[k + 1] - 0.5));
          const rowStart = y * width;
          for (let x = xStart; x <= xEnd; x++) {
            mask[rowStart + x] = 1;
          }
        }
      }
    }

    return mask;
  }

  /**
   * Morphological close on the visible (alpha > 0) mask.
   *
   * Dilates the visible set by `growTransparentPixels` 1-pixel iterations into
   * transparent neighbors (8-connected), then erodes the *dilated* pixels back
   * by `shrinkTransparentPixels` iterations. Originally-rendered pixels are
   * never touched — only previously-transparent pixels can be filled or
   * restored.
   *
   * Symmetric (grow == shrink): outer silhouette returns to ~original; only
   * closes interior holes bounded by enough opaque mass.
   * Asymmetric (shrink < grow): bridges between isolated clusters survive, but
   * the silhouette grows outward by (grow - shrink).
   *
   * Runs before band assignment so newly-filled pixels flow through the rest of
   * the pipeline naturally.
   */
  private growTransparentPass(ctx: DepthSegmentationContext, kwargs?: DepthSegmentationKwargs): DepthSegmentationContext {
    const { gray, alpha, width, height } = ctx;
    if (!gray || !alpha) return ctx;

    const growIterations = kwargs?.growTransparentPixels ?? 0;
    const shrinkIterations = Math.min(kwargs?.shrinkTransparentPixels ?? 0, growIterations);
    if (growIterations <= 0) return ctx;

    const total = width * height;

    // Remember which pixels were originally rendered — erosion must never
    // remove these even if they end up at the eroded boundary.
    const originallyOpaque = new Uint8Array(total);
    for (let i = 0; i < total; i++) {
      if (alpha[i] !== 0) originallyOpaque[i] = 1;
    }

    let srcGray: Uint8Array<ArrayBufferLike> = gray;
    let srcAlpha: Uint8Array<ArrayBufferLike> = alpha;
    let dstGray: Uint8Array<ArrayBufferLike> = new Uint8Array(total);
    let dstAlpha: Uint8Array<ArrayBufferLike> = new Uint8Array(total);

    // ── Dilation: grow visible pixels into transparent ones (8-connected).
    for (let iter = 0; iter < growIterations; iter++) {
      dstGray.set(srcGray);
      dstAlpha.set(srcAlpha);
      let changed = false;

      for (let y = 0; y < height; y++) {
        const rowStart = y * width;
        const hasUp = y > 0;
        const hasDown = y < height - 1;
        for (let x = 0; x < width; x++) {
          const idx = rowStart + x;
          if (srcAlpha[idx] !== 0) continue;

          const hasLeft = x > 0;
          const hasRight = x < width - 1;

          let sum = 0;
          let count = 0;
          if (hasLeft) {
            const nb = idx - 1;
            if (srcAlpha[nb] !== 0) {
              sum += srcGray[nb];
              count++;
            }
          }
          if (hasRight) {
            const nb = idx + 1;
            if (srcAlpha[nb] !== 0) {
              sum += srcGray[nb];
              count++;
            }
          }
          if (hasUp) {
            const nb = idx - width;
            if (srcAlpha[nb] !== 0) {
              sum += srcGray[nb];
              count++;
            }
            if (hasLeft) {
              const nbd = nb - 1;
              if (srcAlpha[nbd] !== 0) {
                sum += srcGray[nbd];
                count++;
              }
            }
            if (hasRight) {
              const nbd = nb + 1;
              if (srcAlpha[nbd] !== 0) {
                sum += srcGray[nbd];
                count++;
              }
            }
          }
          if (hasDown) {
            const nb = idx + width;
            if (srcAlpha[nb] !== 0) {
              sum += srcGray[nb];
              count++;
            }
            if (hasLeft) {
              const nbd = nb - 1;
              if (srcAlpha[nbd] !== 0) {
                sum += srcGray[nbd];
                count++;
              }
            }
            if (hasRight) {
              const nbd = nb + 1;
              if (srcAlpha[nbd] !== 0) {
                sum += srcGray[nbd];
                count++;
              }
            }
          }

          if (count > 0) {
            dstGray[idx] = (sum / count) | 0;
            dstAlpha[idx] = 255;
            changed = true;
          }
        }
      }

      const tmpG = srcGray;
      srcGray = dstGray;
      dstGray = tmpG;
      const tmpA = srcAlpha;
      srcAlpha = dstAlpha;
      dstAlpha = tmpA;

      if (!changed) break;
    }

    // ── Erosion: peel off filled pixels whose 8-neighborhood touches a
    // transparent pixel. Originally-opaque pixels are immune.
    for (let iter = 0; iter < shrinkIterations; iter++) {
      dstAlpha.set(srcAlpha);
      let changed = false;

      for (let y = 0; y < height; y++) {
        const rowStart = y * width;
        for (let x = 0; x < width; x++) {
          const idx = rowStart + x;
          if (srcAlpha[idx] === 0) continue;
          if (originallyOpaque[idx]) continue;

          let touchesTransparent = false;
          if (x === 0 || x === width - 1 || y === 0 || y === height - 1) {
            touchesTransparent = true;
          } else if (srcAlpha[idx - 1] === 0) touchesTransparent = true;
          else if (srcAlpha[idx + 1] === 0) touchesTransparent = true;
          else if (srcAlpha[idx - width] === 0) touchesTransparent = true;
          else if (srcAlpha[idx + width] === 0) touchesTransparent = true;
          else if (srcAlpha[idx - width - 1] === 0) touchesTransparent = true;
          else if (srcAlpha[idx - width + 1] === 0) touchesTransparent = true;
          else if (srcAlpha[idx + width - 1] === 0) touchesTransparent = true;
          else if (srcAlpha[idx + width + 1] === 0) touchesTransparent = true;

          if (touchesTransparent) {
            dstAlpha[idx] = 0;
            changed = true;
          }
        }
      }

      const tmpA = srcAlpha;
      srcAlpha = dstAlpha;
      dstAlpha = tmpA;
      if (!changed) break;
    }

    // Zero gray wherever alpha ended up 0 so band assignment treats it as unset.
    for (let i = 0; i < total; i++) {
      if (srcAlpha[i] === 0) srcGray[i] = 0;
    }

    ctx.gray = srcGray;
    ctx.alpha = srcAlpha;
    return ctx;
  }

  /**
   * Pre-CC stochastic ribbon chopper.
   *
   * Punches a fraction of band-assigned pixels to band=-1 before CC. Long
   * thin ribbons (snakes along walls) are fragile and break easily; large
   * compact regions (rooms) are robust because many parallel paths route
   * around the punched pixels. The downstream `heal_chopper_lines` pass
   * reassigns each punched pixel to its largest neighboring region, so the
   * noise leaves no visual scar in the final output.
   *
   * Uses the raw position map's A byte (low bits of XY eye-position) as
   * the noise source — A is essentially per-pixel noise, so no PRNG is
   * needed. A pixel is punched iff `A < round(fraction * 256)`.
   */
  private preCCNoisePass(ctx: DepthSegmentationContext, kwargs?: DepthSegmentationKwargs): DepthSegmentationContext {
    const { pixelBand, src } = ctx;
    if (!pixelBand || !src) return ctx;

    const fraction = kwargs?.preCCNoiseFraction ?? 0;
    if (fraction <= 0) return ctx;

    const threshold = Math.min(256, Math.max(0, Math.round(fraction * 256)));
    if (threshold === 0) return ctx;

    const total = pixelBand.length;
    const noisePunched = new Uint8Array(total);

    for (let i = 0; i < total; i++) {
      if (pixelBand[i] < 0) continue;
      // src is RGBA — A byte is at index 4*i + 3.
      if (src[i * 4 + 3] < threshold) {
        pixelBand[i] = -1;
        noisePunched[i] = 1;
      }
    }

    ctx.noisePunched = noisePunched;
    return ctx;
  }

  /**
   * Heal pixels that were punched to band=-1 by the pre-CC chopper.
   *
   * For each punched pixel, look at its 4-connected neighbors' `componentId`s,
   * tally region sizes, and reassign to the largest neighboring region. This
   * happens after CC + gap-fill, so all real regions are already established.
   * The chopper has done its job (preventing snake formation during CC) and
   * the heal step erases its visual trace by binding each punched pixel back
   * to whichever big region was nearest.
   *
   * Multi-pass: pixels with no assigned-region neighbor are skipped on the
   * first iteration and revisited after their neighbors get filled, so a
   * full grid intersection (where every immediate neighbor was also punched)
   * still heals after a few passes. Bails when no progress is made.
   */
  private healChopperLinesPass(ctx: DepthSegmentationContext): DepthSegmentationContext {
    const { componentId, noisePunched, regionSizes, regionCentroids, edgeTouchingRegions, width, height } = ctx;
    if (!componentId || !noisePunched || !regionSizes) return ctx;

    // Iterate to a fixed point: each pass heals every punched pixel with at
    // least one labeled neighbor. Max iterations bounded by spacing — even
    // a fully punched grid intersection is only ~2 deep.
    for (let iter = 0; iter < 8; iter++) {
      let changed = false;
      for (let i = 0; i < componentId.length; i++) {
        if (noisePunched[i] === 0 || componentId[i] !== 0) continue;
        const x = i % width;
        const y = (i - x) / width;

        // Tally neighbor region IDs by *region size* (largest wins), per the
        // user's spec. Fall back to majority count on a tie.
        let bestId = 0;
        let bestSize = -1;
        const checkNb = (nb: number): void => {
          const rid = componentId[nb];
          if (rid === 0) return;
          const size = regionSizes.get(rid) ?? 0;
          if (size > bestSize) {
            bestSize = size;
            bestId = rid;
          }
        };
        if (x > 0) checkNb(i - 1);
        if (x < width - 1) checkNb(i + 1);
        if (y > 0) checkNb(i - width);
        if (y < height - 1) checkNb(i + width);

        if (bestId !== 0) {
          componentId[i] = bestId;
          regionSizes.set(bestId, (regionSizes.get(bestId) ?? 0) + 1);
          const centroid = regionCentroids?.get(bestId);
          if (centroid) {
            centroid.sumX += x;
            centroid.sumY += y;
          }
          if (edgeTouchingRegions && (x === 0 || x === width - 1 || y === 0 || y === height - 1)) {
            edgeTouchingRegions.add(bestId);
          }
          changed = true;
        }
      }
      if (!changed) break;
    }

    return ctx;
  }

  /**
   * Assign pixels to depth bands based on grayscale values.
   */
  private bandAssignmentPass(ctx: DepthSegmentationContext, kwargs?: DepthSegmentationKwargs): DepthSegmentationContext {
    const { gray, alpha, width, height } = ctx;
    if (!gray || !alpha) return ctx;

    const bandCount = kwargs?.bandCount ?? 16;
    const total = width * height;
    const bandSize = 256 / bandCount;
    const pixelBand = new Int32Array(total);

    for (let i = 0; i < total; i++) {
      if (alpha[i] === 0) {
        pixelBand[i] = -1;
      } else {
        pixelBand[i] = Math.min(bandCount - 1, Math.floor(gray[i] / bandSize));
      }
    }

    return { ...ctx, pixelBand };
  }

  /**
   * Run BFS connected components on banded pixels.
   */
  private connectedComponentsPass(ctx: DepthSegmentationContext, kwargs?: DepthSegmentationKwargs): DepthSegmentationContext {
    const { pixelBand, width, height } = ctx;
    if (!pixelBand) return ctx;

    const total = width * height;
    const minRegionPixelsPercent = kwargs?.minRegionPixelsPercent ?? 0.005;
    const minRegionPixelsAbsolute = kwargs?.minRegionPixelsAbsolute ?? 50;
    const minRegionPixels = Math.max(minRegionPixelsAbsolute, Math.round(total * minRegionPixelsPercent));
    const ignoreBandsBelow = kwargs?.ignoreBandsBelow ?? 0;

    const componentId = new Int32Array(total);
    let nextRegionId = 1;

    const regionSizes = new Map<number, number>();
    const regionBands = new Map<number, number>();
    const regionCentroids = new Map<number, { sumX: number; sumY: number }>();
    const regionSamplePixel: number[] = [];
    const edgeTouchingRegions = new Set<number>();
    const stack: number[] = [];

    for (let start = 0; start < total; start++) {
      if (pixelBand[start] < 0 || pixelBand[start] < ignoreBandsBelow || componentId[start] !== 0) continue;

      const band = pixelBand[start];
      const regId = nextRegionId++;

      componentId[start] = regId;
      stack.length = 0;
      stack.push(start);
      let size = 0;
      let touchesEdge = false;
      let sumX = 0;
      let sumY = 0;

      while (stack.length > 0) {
        const idx = stack.pop() as number;
        size++;
        const x = idx % width;
        const y = (idx - x) / width;
        sumX += x;
        sumY += y;
        if (x === 0 || x === width - 1 || y === 0 || y === height - 1) touchesEdge = true;

        // 4-connected neighbors
        if (x > 0) {
          const nb = idx - 1;
          if (componentId[nb] === 0 && pixelBand[nb] === band) {
            componentId[nb] = regId;
            stack.push(nb);
          }
        }
        if (x < width - 1) {
          const nb = idx + 1;
          if (componentId[nb] === 0 && pixelBand[nb] === band) {
            componentId[nb] = regId;
            stack.push(nb);
          }
        }
        if (y > 0) {
          const nb = idx - width;
          if (componentId[nb] === 0 && pixelBand[nb] === band) {
            componentId[nb] = regId;
            stack.push(nb);
          }
        }
        if (y < height - 1) {
          const nb = idx + width;
          if (componentId[nb] === 0 && pixelBand[nb] === band) {
            componentId[nb] = regId;
            stack.push(nb);
          }
        }
      }

      regionSizes.set(regId, size);
      regionBands.set(regId, band);
      regionCentroids.set(regId, { sumX, sumY });
      if (touchesEdge) edgeTouchingRegions.add(regId);
      regionSamplePixel[regId] = start;
    }

    return {
      ...ctx,
      componentId,
      regionSizes,
      regionBands,
      regionCentroids,
      regionSamplePixel,
      edgeTouchingRegions,
      minRegionPixels,
    };
  }

  /**
   * Fill small unassigned regions (componentId === 0) by assigning them to a neighboring region.
   * This helps clean up small gaps between regions caused by depth discontinuities.
   */
  private fillSmallGapsPass(ctx: DepthSegmentationContext, kwargs?: DepthSegmentationKwargs): DepthSegmentationContext {
    const { componentId, width, height, regionSizes, regionBands, regionCentroids, edgeTouchingRegions } = ctx;
    if (!componentId || !regionSizes || !regionBands || !regionCentroids || !edgeTouchingRegions) return ctx;

    const maxGapSize = kwargs?.fillSmallGapsMaxPixels ?? 20;
    const total = width * height;

    // Find all unassigned pixels (componentId === 0) and group them into connected components
    const visited = new Uint8Array(total);
    const queue: number[] = [];

    for (let startIdx = 0; startIdx < total; startIdx++) {
      if (componentId[startIdx] !== 0 || visited[startIdx]) continue;

      // BFS to find all connected unassigned pixels
      const gapPixels: number[] = [];
      const neighborRegions = new Map<number, number>(); // regionId -> count of adjacent pixels

      queue.length = 0;
      queue.push(startIdx);
      visited[startIdx] = 1;

      while (queue.length > 0) {
        const idx = queue.shift() as number;
        gapPixels.push(idx);

        const x = idx % width;
        const y = Math.floor(idx / width);

        // Check 4-connected neighbors
        const neighbors: [number, number][] = [
          [x + 1, y],
          [x - 1, y],
          [x, y + 1],
          [x, y - 1],
        ];

        for (const [nx, ny] of neighbors) {
          if (nx < 0 || nx >= width || ny < 0 || ny >= height) continue;
          const nIdx = ny * width + nx;

          if (componentId[nIdx] === 0) {
            // Another unassigned pixel - add to gap
            if (!visited[nIdx]) {
              visited[nIdx] = 1;
              queue.push(nIdx);
            }
          } else {
            // Adjacent to an assigned region - count it
            const neighborRegionId = componentId[nIdx];
            neighborRegions.set(neighborRegionId, (neighborRegions.get(neighborRegionId) || 0) + 1);
          }
        }
      }

      // Only fill small gaps
      if (gapPixels.length > maxGapSize) continue;

      // Find the neighbor region with the most adjacent pixels
      let bestRegionId = 0;
      let bestCount = 0;
      for (const [regionId, count] of neighborRegions) {
        if (count > bestCount) {
          bestCount = count;
          bestRegionId = regionId;
        }
      }

      // Assign all gap pixels to the best neighbor region
      if (bestRegionId !== 0) {
        for (const idx of gapPixels) {
          componentId[idx] = bestRegionId;
        }

        // Update region size
        const currentSize = regionSizes.get(bestRegionId) || 0;
        regionSizes.set(bestRegionId, currentSize + gapPixels.length);

        // Update centroid
        const centroid = regionCentroids.get(bestRegionId);
        if (centroid) {
          for (const idx of gapPixels) {
            const x = idx % width;
            const y = Math.floor(idx / width);
            centroid.sumX += x;
            centroid.sumY += y;
          }
        }
      }
    }

    return ctx;
  }

  /**
   * Split non-compact (snaking) regions.
   *
   * Two-stage algorithm so the splitter works on both dumbbell-shaped regions
   * (two fat blobs joined by a thin neck) AND uniform-width ribbons (wall
   * snakes that follow walls at a constant depth band):
   *
   * 1. **Iterative erosion**: erode the region's binary mask by 1 px at a
   *    time, up to `splitSnakingErosionRadius` iterations. After each step,
   *    count connected components of the eroded mask. The depth that
   *    produces the most components ≥ 1 wins; if that's >1, multi-source
   *    BFS reassigns original pixels to the nearest eroded component.
   *    This catches dumbbells: as soon as the neck is thinner than the
   *    erosion depth, the two ends fall apart.
   *
   * 2. **PCA fallback (chunking)**: if erosion never splits the region (it
   *    just shrinks uniformly), check the long/short-axis ratio via
   *    eigen-decomposition of the pixel covariance matrix. If the ratio
   *    exceeds 4, chop the ribbon perpendicular to its long axis into
   *    `round(longAxis / shortAxis)` equal chunks. Each pixel is assigned
   *    by its projection onto the long axis. Round rooms (low aspect
   *    ratio) are never touched.
   */
  private splitSnakingRegionsPass(ctx: DepthSegmentationContext, kwargs?: DepthSegmentationKwargs): DepthSegmentationContext {
    const { componentId, regionSizes, regionBands, regionCentroids, edgeTouchingRegions, width, height } = ctx;
    if (!componentId || !regionSizes || !regionBands || !regionCentroids || !edgeTouchingRegions) return ctx;

    const compactnessThreshold = kwargs?.splitSnakingCompactnessThreshold ?? 0.15;
    const erosionRadius = kwargs?.splitSnakingErosionRadius ?? 3;
    const minRegionSize = kwargs?.splitSnakingMinRegionSize ?? 200;
    const total = width * height;

    // Single pass: compute perimeter pixels and bounding boxes per region
    const perimeterCounts = new Map<number, number>();
    const regionBBox = new Map<number, { minX: number; maxX: number; minY: number; maxY: number }>();

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const idx = y * width + x;
        const regId = componentId[idx];
        if (regId === 0) continue;

        // Track bounding box
        const bbox = regionBBox.get(regId);
        if (!bbox) {
          regionBBox.set(regId, { minX: x, maxX: x, minY: y, maxY: y });
        } else {
          if (x < bbox.minX) bbox.minX = x;
          if (x > bbox.maxX) bbox.maxX = x;
          if (y < bbox.minY) bbox.minY = y;
          if (y > bbox.maxY) bbox.maxY = y;
        }

        // Check 4-connected neighbors for boundary
        let isBoundary = false;
        if (x === 0 || x === width - 1 || y === 0 || y === height - 1) {
          isBoundary = true;
        } else {
          if (componentId[idx - 1] !== regId) isBoundary = true;
          else if (componentId[idx + 1] !== regId) isBoundary = true;
          else if (componentId[idx - width] !== regId) isBoundary = true;
          else if (componentId[idx + width] !== regId) isBoundary = true;
        }

        if (isBoundary) {
          perimeterCounts.set(regId, (perimeterCounts.get(regId) || 0) + 1);
        }
      }
    }

    // Identify snaking candidates
    const candidates: number[] = [];
    for (const [regId, size] of regionSizes) {
      if (size < minRegionSize) continue;
      const perimeter = perimeterCounts.get(regId) || 1;
      const compactness = (4 * Math.PI * size) / (perimeter * perimeter);
      if (compactness < compactnessThreshold) {
        candidates.push(regId);
      }
    }

    if (candidates.length === 0) return ctx;

    // Track max region ID for assigning new IDs
    let maxRegionId = 0;
    for (const [regId] of regionSizes) {
      if (regId > maxRegionId) maxRegionId = regId;
    }

    // Hoist all per-candidate scratch buffers. Sized to the maximum bbox area
    // across candidates so every candidate can reuse them. One allocation per
    // pass instead of ~10 per candidate. Padding is now a fixed 1 (just
    // enough for the distance transform to see a background ring); the
    // erosion *threshold* doesn't need padding, so high `erosionRadius` no
    // longer inflates the working area.
    const PAD = 1;
    let maxLW = 0;
    let maxLH = 0;
    let maxArea = 0;
    for (const candidateRegId of candidates) {
      const bbox = regionBBox.get(candidateRegId);
      if (!bbox) continue;
      const lw = Math.min(width - 1, bbox.maxX + PAD) - Math.max(0, bbox.minX - PAD) + 1;
      const lh = Math.min(height - 1, bbox.maxY + PAD) - Math.max(0, bbox.minY - PAD) + 1;
      if (lw > maxLW) maxLW = lw;
      if (lh > maxLH) maxLH = lh;
      if (lw * lh > maxArea) maxArea = lw * lh;
    }
    const maxDistCap = maxLW + maxLH + 2;

    const mask = new Uint8Array(maxArea);
    const dist = new Int32Array(maxArea);
    const parent = new Int32Array(maxArea);
    const compSize = new Int32Array(maxArea);
    const sortedPixels = new Int32Array(maxArea);
    const bucketCount = new Int32Array(maxDistCap);
    const bucketStart = new Int32Array(maxDistCap);
    const bucketPos = new Int32Array(maxDistCap);
    const assignment = new Int32Array(maxArea);
    const bfsQueue = new Int32Array(maxArea);

    for (const candidateRegId of candidates) {
      const bbox = regionBBox.get(candidateRegId);
      if (!bbox) continue;

      const lx0 = Math.max(0, bbox.minX - PAD);
      const ly0 = Math.max(0, bbox.minY - PAD);
      const lx1 = Math.min(width - 1, bbox.maxX + PAD);
      const ly1 = Math.min(height - 1, bbox.maxY + PAD);
      const lw = lx1 - lx0 + 1;
      const lh = ly1 - ly0 + 1;
      const area = lw * lh;

      // Reset only the prefix of each buffer that this candidate touches.
      mask.fill(0, 0, area);
      assignment.fill(0, 0, area);
      for (let y = ly0; y <= ly1; y++) {
        for (let x = lx0; x <= lx1; x++) {
          if (componentId[y * width + x] === candidateRegId) {
            mask[(y - ly0) * lw + (x - lx0)] = 1;
          }
        }
      }

      // ── Stage 1: iterative erosion ────────────────────────────────────────
      // Chebyshev distance transform via two-pass min-of-neighbors+1.
      const INF = lw + lh;
      for (let i = 0; i < area; i++) {
        dist[i] = mask[i] ? INF : 0;
      }
      for (let ly = 0; ly < lh; ly++) {
        for (let lx = 0; lx < lw; lx++) {
          const i = ly * lw + lx;
          if (dist[i] === 0) continue;
          if (ly > 0) {
            const v = dist[i - lw] + 1;
            if (v < dist[i]) dist[i] = v;
          }
          if (lx > 0) {
            const v = dist[i - 1] + 1;
            if (v < dist[i]) dist[i] = v;
          }
          if (ly > 0 && lx > 0) {
            const v = dist[i - lw - 1] + 1;
            if (v < dist[i]) dist[i] = v;
          }
          if (ly > 0 && lx < lw - 1) {
            const v = dist[i - lw + 1] + 1;
            if (v < dist[i]) dist[i] = v;
          }
        }
      }
      for (let ly = lh - 1; ly >= 0; ly--) {
        for (let lx = lw - 1; lx >= 0; lx--) {
          const i = ly * lw + lx;
          if (dist[i] === 0) continue;
          if (ly < lh - 1) {
            const v = dist[i + lw] + 1;
            if (v < dist[i]) dist[i] = v;
          }
          if (lx < lw - 1) {
            const v = dist[i + 1] + 1;
            if (v < dist[i]) dist[i] = v;
          }
          if (ly < lh - 1 && lx < lw - 1) {
            const v = dist[i + lw + 1] + 1;
            if (v < dist[i]) dist[i] = v;
          }
          if (ly < lh - 1 && lx > 0) {
            const v = dist[i + lw - 1] + 1;
            if (v < dist[i]) dist[i] = v;
          }
        }
      }

      // Sweep erosion depths via union-find on pixels in decreasing dist
      // order. Invariant: after processing all pixels with dist ≥ D, the
      // union-find roots are exactly the components of the eroded mask at
      // depth D. We only track the *depth* that maximized liveCount; the
      // labels at that depth are recovered by a replay pass below (no
      // snapshot needed).
      const minErodedComponentSize = Math.max(8, Math.floor(minRegionSize * 0.05));

      let maxDist = 0;
      for (let i = 0; i < area; i++) {
        const d = dist[i];
        if (d > maxDist) maxDist = d;
      }
      const cappedMaxDepth = Math.min(maxDist, erosionRadius);

      // Counting sort buckets (descending by dist).
      bucketCount.fill(0, 0, maxDist + 2);
      for (let i = 0; i < area; i++) {
        if (dist[i] > 0) bucketCount[dist[i]]++;
      }
      let cursor = 0;
      for (let d = maxDist; d >= 1; d--) {
        bucketStart[d] = cursor;
        cursor += bucketCount[d];
      }
      for (let d = 0; d <= maxDist + 1; d++) bucketPos[d] = bucketStart[d];
      for (let i = 0; i < area; i++) {
        const d = dist[i];
        if (d > 0) sortedPixels[bucketPos[d]++] = i;
      }

      // Helper: sweep pixels from dist=maxDist down to dist=stopDepth,
      // unioning each with already-added neighbors. `parent` and `compSize`
      // must be pre-zeroed for indices 0..area-1; we use parent[i] = -1
      // sentinel to mean "not added yet". Returns the depth at which
      // liveCount peaked.
      // We need parent[] = -1 for the active area only.
      for (let i = 0; i < area; i++) parent[i] = -1;

      let bestDepth = 0;
      let bestComponentCount = 1;
      let liveComponentCount = 0;

      for (let d = maxDist; d >= 1; d--) {
        const start = bucketStart[d];
        const end = start + bucketCount[d];
        for (let k = start; k < end; k++) {
          const idx = sortedPixels[k];
          parent[idx] = idx;
          compSize[idx] = 1;
          // 1 >= minErodedComponentSize is false for any reasonable minSize
          // (≥8), so a fresh pixel doesn't contribute to liveCount. If you
          // ever set minErodedComponentSize = 1, uncomment:
          // liveComponentCount++;

          const lx = idx % lw;
          const ly = (idx - lx) / lw;

          // Inline 4-neighbor union. Each branch: bounds-check, parent-check,
          // then union-by-size with inline path compression in find().
          // liveCount updates folded into the size comparisons.

          // West
          if (lx > 0) {
            const nb = idx - 1;
            if (parent[nb] !== -1) {
              // find(idx)
              let rA = idx;
              while (parent[rA] !== rA) rA = parent[rA];
              let cur = idx;
              while (parent[cur] !== rA) {
                const nx = parent[cur];
                parent[cur] = rA;
                cur = nx;
              }
              // find(nb)
              let rB = nb;
              while (parent[rB] !== rB) rB = parent[rB];
              cur = nb;
              while (parent[cur] !== rB) {
                const nx = parent[cur];
                parent[cur] = rB;
                cur = nx;
              }
              if (rA !== rB) {
                const sA = compSize[rA];
                const sB = compSize[rB];
                const merged = sA + sB;
                // Inlined liveCount delta: -1 per pre-live root, +1 if merged is live.
                const wasALive = sA >= minErodedComponentSize ? 1 : 0;
                const wasBLive = sB >= minErodedComponentSize ? 1 : 0;
                const isMergedLive = merged >= minErodedComponentSize ? 1 : 0;
                liveComponentCount += isMergedLive - wasALive - wasBLive;
                if (sA >= sB) {
                  parent[rB] = rA;
                  compSize[rA] = merged;
                } else {
                  parent[rA] = rB;
                  compSize[rB] = merged;
                }
              }
            }
          }
          // East
          if (lx < lw - 1) {
            const nb = idx + 1;
            if (parent[nb] !== -1) {
              let rA = idx;
              while (parent[rA] !== rA) rA = parent[rA];
              let cur = idx;
              while (parent[cur] !== rA) {
                const nx = parent[cur];
                parent[cur] = rA;
                cur = nx;
              }
              let rB = nb;
              while (parent[rB] !== rB) rB = parent[rB];
              cur = nb;
              while (parent[cur] !== rB) {
                const nx = parent[cur];
                parent[cur] = rB;
                cur = nx;
              }
              if (rA !== rB) {
                const sA = compSize[rA];
                const sB = compSize[rB];
                const merged = sA + sB;
                const wasALive = sA >= minErodedComponentSize ? 1 : 0;
                const wasBLive = sB >= minErodedComponentSize ? 1 : 0;
                const isMergedLive = merged >= minErodedComponentSize ? 1 : 0;
                liveComponentCount += isMergedLive - wasALive - wasBLive;
                if (sA >= sB) {
                  parent[rB] = rA;
                  compSize[rA] = merged;
                } else {
                  parent[rA] = rB;
                  compSize[rB] = merged;
                }
              }
            }
          }
          // North
          if (ly > 0) {
            const nb = idx - lw;
            if (parent[nb] !== -1) {
              let rA = idx;
              while (parent[rA] !== rA) rA = parent[rA];
              let cur = idx;
              while (parent[cur] !== rA) {
                const nx = parent[cur];
                parent[cur] = rA;
                cur = nx;
              }
              let rB = nb;
              while (parent[rB] !== rB) rB = parent[rB];
              cur = nb;
              while (parent[cur] !== rB) {
                const nx = parent[cur];
                parent[cur] = rB;
                cur = nx;
              }
              if (rA !== rB) {
                const sA = compSize[rA];
                const sB = compSize[rB];
                const merged = sA + sB;
                const wasALive = sA >= minErodedComponentSize ? 1 : 0;
                const wasBLive = sB >= minErodedComponentSize ? 1 : 0;
                const isMergedLive = merged >= minErodedComponentSize ? 1 : 0;
                liveComponentCount += isMergedLive - wasALive - wasBLive;
                if (sA >= sB) {
                  parent[rB] = rA;
                  compSize[rA] = merged;
                } else {
                  parent[rA] = rB;
                  compSize[rB] = merged;
                }
              }
            }
          }
          // South
          if (ly < lh - 1) {
            const nb = idx + lw;
            if (parent[nb] !== -1) {
              let rA = idx;
              while (parent[rA] !== rA) rA = parent[rA];
              let cur = idx;
              while (parent[cur] !== rA) {
                const nx = parent[cur];
                parent[cur] = rA;
                cur = nx;
              }
              let rB = nb;
              while (parent[rB] !== rB) rB = parent[rB];
              cur = nb;
              while (parent[cur] !== rB) {
                const nx = parent[cur];
                parent[cur] = rB;
                cur = nx;
              }
              if (rA !== rB) {
                const sA = compSize[rA];
                const sB = compSize[rB];
                const merged = sA + sB;
                const wasALive = sA >= minErodedComponentSize ? 1 : 0;
                const wasBLive = sB >= minErodedComponentSize ? 1 : 0;
                const isMergedLive = merged >= minErodedComponentSize ? 1 : 0;
                liveComponentCount += isMergedLive - wasALive - wasBLive;
                if (sA >= sB) {
                  parent[rB] = rA;
                  compSize[rA] = merged;
                } else {
                  parent[rA] = rB;
                  compSize[rB] = merged;
                }
              }
            }
          }
        }

        if (d <= cappedMaxDepth && liveComponentCount > bestComponentCount) {
          bestComponentCount = liveComponentCount;
          bestDepth = d;
        }
      }

      // Replay: re-run union-find but stop sweeping once we've consumed every
      // pixel with dist ≥ bestDepth. Snapshot avoidance — same O(N) work.
      let assignmentNextLabel: number;
      let usedErosion = false;

      if (bestComponentCount >= 2 && bestDepth > 0) {
        for (let i = 0; i < area; i++) parent[i] = -1;
        for (let d = maxDist; d >= bestDepth; d--) {
          const start = bucketStart[d];
          const end = start + bucketCount[d];
          for (let k = start; k < end; k++) {
            const idx = sortedPixels[k];
            parent[idx] = idx;
            compSize[idx] = 1;
            const lx = idx % lw;
            const ly = (idx - lx) / lw;
            // Unrolled 4-neighbor union (no liveCount tracking on replay).
            if (lx > 0) {
              const nb = idx - 1;
              if (parent[nb] !== -1) {
                let rA = idx;
                while (parent[rA] !== rA) rA = parent[rA];
                let cur = idx;
                while (parent[cur] !== rA) {
                  const nx = parent[cur];
                  parent[cur] = rA;
                  cur = nx;
                }
                let rB = nb;
                while (parent[rB] !== rB) rB = parent[rB];
                cur = nb;
                while (parent[cur] !== rB) {
                  const nx = parent[cur];
                  parent[cur] = rB;
                  cur = nx;
                }
                if (rA !== rB) {
                  const sA = compSize[rA];
                  const sB = compSize[rB];
                  if (sA >= sB) {
                    parent[rB] = rA;
                    compSize[rA] = sA + sB;
                  } else {
                    parent[rA] = rB;
                    compSize[rB] = sA + sB;
                  }
                }
              }
            }
            if (lx < lw - 1) {
              const nb = idx + 1;
              if (parent[nb] !== -1) {
                let rA = idx;
                while (parent[rA] !== rA) rA = parent[rA];
                let cur = idx;
                while (parent[cur] !== rA) {
                  const nx = parent[cur];
                  parent[cur] = rA;
                  cur = nx;
                }
                let rB = nb;
                while (parent[rB] !== rB) rB = parent[rB];
                cur = nb;
                while (parent[cur] !== rB) {
                  const nx = parent[cur];
                  parent[cur] = rB;
                  cur = nx;
                }
                if (rA !== rB) {
                  const sA = compSize[rA];
                  const sB = compSize[rB];
                  if (sA >= sB) {
                    parent[rB] = rA;
                    compSize[rA] = sA + sB;
                  } else {
                    parent[rA] = rB;
                    compSize[rB] = sA + sB;
                  }
                }
              }
            }
            if (ly > 0) {
              const nb = idx - lw;
              if (parent[nb] !== -1) {
                let rA = idx;
                while (parent[rA] !== rA) rA = parent[rA];
                let cur = idx;
                while (parent[cur] !== rA) {
                  const nx = parent[cur];
                  parent[cur] = rA;
                  cur = nx;
                }
                let rB = nb;
                while (parent[rB] !== rB) rB = parent[rB];
                cur = nb;
                while (parent[cur] !== rB) {
                  const nx = parent[cur];
                  parent[cur] = rB;
                  cur = nx;
                }
                if (rA !== rB) {
                  const sA = compSize[rA];
                  const sB = compSize[rB];
                  if (sA >= sB) {
                    parent[rB] = rA;
                    compSize[rA] = sA + sB;
                  } else {
                    parent[rA] = rB;
                    compSize[rB] = sA + sB;
                  }
                }
              }
            }
            if (ly < lh - 1) {
              const nb = idx + lw;
              if (parent[nb] !== -1) {
                let rA = idx;
                while (parent[rA] !== rA) rA = parent[rA];
                let cur = idx;
                while (parent[cur] !== rA) {
                  const nx = parent[cur];
                  parent[cur] = rA;
                  cur = nx;
                }
                let rB = nb;
                while (parent[rB] !== rB) rB = parent[rB];
                cur = nb;
                while (parent[cur] !== rB) {
                  const nx = parent[cur];
                  parent[cur] = rB;
                  cur = nx;
                }
                if (rA !== rB) {
                  const sA = compSize[rA];
                  const sB = compSize[rB];
                  if (sA >= sB) {
                    parent[rB] = rA;
                    compSize[rA] = sA + sB;
                  } else {
                    parent[rA] = rB;
                    compSize[rB] = sA + sB;
                  }
                }
              }
            }
          }
        }

        // Label roots whose final component is ≥ minErodedComponentSize, then
        // multi-source BFS to fill remaining mask pixels with the nearest
        // eroded-component label.
        const rootToLabel = new Map<number, number>();
        let nextOutLabel = 1;
        let queueTail = 0;
        for (let i = 0; i < area; i++) {
          if (parent[i] === -1) continue;
          let r = i;
          while (parent[r] !== r) r = parent[r];
          if (compSize[r] < minErodedComponentSize) continue;
          let label = rootToLabel.get(r);
          if (label === undefined) {
            label = nextOutLabel++;
            rootToLabel.set(r, label);
          }
          assignment[i] = label;
          bfsQueue[queueTail++] = i;
        }

        if (nextOutLabel > 2) {
          usedErosion = true;
          let head = 0;
          while (head < queueTail) {
            const idx = bfsQueue[head++];
            const label = assignment[idx];
            const lx = idx % lw;
            const ly = (idx - lx) / lw;
            if (lx > 0 && assignment[idx - 1] === 0 && mask[idx - 1]) {
              assignment[idx - 1] = label;
              bfsQueue[queueTail++] = idx - 1;
            }
            if (lx < lw - 1 && assignment[idx + 1] === 0 && mask[idx + 1]) {
              assignment[idx + 1] = label;
              bfsQueue[queueTail++] = idx + 1;
            }
            if (ly > 0 && assignment[idx - lw] === 0 && mask[idx - lw]) {
              assignment[idx - lw] = label;
              bfsQueue[queueTail++] = idx - lw;
            }
            if (ly < lh - 1 && assignment[idx + lw] === 0 && mask[idx + lw]) {
              assignment[idx + lw] = label;
              bfsQueue[queueTail++] = idx + lw;
            }
          }
          assignmentNextLabel = nextOutLabel;
        } else {
          // Erosion split survived only one real lobe (others were noise).
          assignment.fill(0, 0, area);
          assignmentNextLabel = 1; // forces PCA fallback below
        }
      } else {
        assignmentNextLabel = 1;
      }

      if (!usedErosion) {
        // PCA: compute covariance of pixel positions, find principal axis.
        let sumX = 0;
        let sumY = 0;
        let count = 0;
        for (let i = 0; i < lw * lh; i++) {
          if (mask[i] === 0) continue;
          const lx = i % lw;
          const ly = (i - lx) / lw;
          sumX += lx;
          sumY += ly;
          count++;
        }
        if (count === 0) continue;
        const meanX = sumX / count;
        const meanY = sumY / count;

        let cxx = 0;
        let cxy = 0;
        let cyy = 0;
        for (let i = 0; i < lw * lh; i++) {
          if (mask[i] === 0) continue;
          const lx = i % lw;
          const ly = (i - lx) / lw;
          const dx = lx - meanX;
          const dy = ly - meanY;
          cxx += dx * dx;
          cxy += dx * dy;
          cyy += dy * dy;
        }
        cxx /= count;
        cxy /= count;
        cyy /= count;

        // Eigenvalues of [[cxx, cxy], [cxy, cyy]]
        const trace = cxx + cyy;
        const det = cxx * cyy - cxy * cxy;
        const disc = Math.max(0, (trace * trace) / 4 - det);
        const sqrtDisc = Math.sqrt(disc);
        const lambdaLong = trace / 2 + sqrtDisc;
        const lambdaShort = Math.max(trace / 2 - sqrtDisc, 1e-6);

        // Standard deviations along principal axes; ratio = aspect.
        const stdLong = Math.sqrt(lambdaLong);
        const stdShort = Math.sqrt(lambdaShort);
        const aspect = stdLong / stdShort;

        if (aspect < 4) {
          // Round-ish region; nothing to split.
          continue;
        }

        // Long-axis unit vector (eigenvector of lambdaLong).
        let axisX: number;
        let axisY: number;
        if (Math.abs(cxy) > 1e-6) {
          axisX = lambdaLong - cyy;
          axisY = cxy;
        } else {
          axisX = cxx >= cyy ? 1 : 0;
          axisY = cxx >= cyy ? 0 : 1;
        }
        const axisLen = Math.hypot(axisX, axisY) || 1;
        axisX /= axisLen;
        axisY /= axisLen;

        const chunkCount = Math.max(2, Math.round(aspect));

        // Project every pixel onto the long axis; find min/max for binning.
        let projMin = Infinity;
        let projMax = -Infinity;
        for (let i = 0; i < lw * lh; i++) {
          if (mask[i] === 0) continue;
          const lx = i % lw;
          const ly = (i - lx) / lw;
          const proj = (lx - meanX) * axisX + (ly - meanY) * axisY;
          if (proj < projMin) projMin = proj;
          if (proj > projMax) projMax = proj;
        }
        const projRange = Math.max(projMax - projMin, 1e-6);

        for (let i = 0; i < lw * lh; i++) {
          if (mask[i] === 0) continue;
          const lx = i % lw;
          const ly = (i - lx) / lw;
          const proj = (lx - meanX) * axisX + (ly - meanY) * axisY;
          const bin = Math.min(chunkCount - 1, Math.floor(((proj - projMin) / projRange) * chunkCount));
          assignment[i] = bin + 1;
        }
        assignmentNextLabel = chunkCount + 1;
      }

      const nextLabel = assignmentNextLabel;
      if (nextLabel <= 2) continue;

      // Map eroded labels to global region IDs
      const band = regionBands.get(candidateRegId) ?? 0;
      const labelToGlobalId = new Map<number, number>();
      labelToGlobalId.set(1, candidateRegId); // first component keeps original ID

      for (let label = 2; label < nextLabel; label++) {
        maxRegionId++;
        labelToGlobalId.set(label, maxRegionId);
      }

      // Reset tracking for original region
      regionSizes.set(candidateRegId, 0);
      regionCentroids.set(candidateRegId, { sumX: 0, sumY: 0 });
      edgeTouchingRegions.delete(candidateRegId);

      // Initialize tracking for new regions
      for (let label = 2; label < nextLabel; label++) {
        const newId = labelToGlobalId.get(label);
        if (newId === undefined) continue;
        regionSizes.set(newId, 0);
        regionBands.set(newId, band);
        regionCentroids.set(newId, { sumX: 0, sumY: 0 });
      }

      // Apply assignment to global componentId and update tracking
      for (let ly = 0; ly < lh; ly++) {
        for (let lx = 0; lx < lw; lx++) {
          const localIdx = ly * lw + lx;
          if (mask[localIdx] === 0 || assignment[localIdx] === 0) continue;

          const globalId = labelToGlobalId.get(assignment[localIdx]);
          if (globalId === undefined) continue;
          const gx = lx + lx0;
          const gy = ly + ly0;
          componentId[gy * width + gx] = globalId;

          regionSizes.set(globalId, (regionSizes.get(globalId) || 0) + 1);

          const centroid = regionCentroids.get(globalId);
          if (!centroid) continue;
          centroid.sumX += gx;
          centroid.sumY += gy;

          if (gx === 0 || gx === width - 1 || gy === 0 || gy === height - 1) {
            edgeTouchingRegions.add(globalId);
          }
        }
      }
    }

    // Update regionSamplePixel for all regions (some may have been reassigned)
    if (ctx.regionSamplePixel) {
      for (let i = 0; i < total; i++) {
        const regId = componentId[i];
        if (regId === 0) continue;
        if (ctx.regionSamplePixel[regId] === undefined || componentId[ctx.regionSamplePixel[regId]] !== regId) {
          ctx.regionSamplePixel[regId] = i;
        }
      }
    }

    return ctx;
  }

  /**
   * Build colored lookup data and region pixel lists.
   */
  private coloredDataPass(ctx: DepthSegmentationContext): DepthSegmentationContext {
    const { componentId, width, height } = ctx;
    if (!componentId) return ctx;

    const total = width * height;
    const coloredData = new Uint8ClampedArray(total * 4);
    const regionColors = new Map<number, number>();
    const regionPixels = new Map<number, number[]>();

    for (let i = 0; i < total; i++) {
      const regId = componentId[i];
      const pi = i * 4;

      if (regId === 0) {
        coloredData[pi + 3] = 255;
        continue;
      }

      const r = regId & 0xff;
      const g = (regId >> 8) & 0xff;
      const b = (regId >> 16) & 0xff;
      coloredData[pi] = r;
      coloredData[pi + 1] = g;
      coloredData[pi + 2] = b;
      coloredData[pi + 3] = 255;

      const colorKey = r | (g << 8) | (b << 16);
      if (!regionColors.has(colorKey)) regionColors.set(colorKey, regId);

      let pixels = regionPixels.get(regId);
      if (!pixels) {
        pixels = [];
        regionPixels.set(regId, pixels);
      }
      pixels.push(i);
    }

    return { ...ctx, coloredData, regionColors, regionPixels };
  }

  /**
   * Build the region adjacency graph.
   */
  private regionGraphPass(ctx: DepthSegmentationContext, kwargs?: DepthSegmentationKwargs): DepthSegmentationContext {
    const { componentId, width, height, regionSizes, regionBands, regionCentroids, regionSamplePixel, edgeTouchingRegions, minRegionPixels } = ctx;

    if (!componentId || !regionSizes || !regionBands || !regionCentroids || !regionSamplePixel || !edgeTouchingRegions || minRegionPixels === undefined) {
      return ctx;
    }

    const nodes = new Map<number, RegionGraphNode>();

    // First pass: compute perimeter pixels and adjacency counts
    const adjacency = new Map<number, Map<number, number>>();
    const perimeterCounts = new Map<number, number>();

    // Initialize maps for all regions (no size filtering for region graph)
    for (const [regionId] of regionSizes) {
      adjacency.set(regionId, new Map());
      perimeterCounts.set(regionId, 0);
    }

    // Scan all pixels to find boundary pixels and adjacencies
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const idx = y * width + x;
        const regionId = componentId[idx];

        if (regionId === 0) continue;
        const size = regionSizes.get(regionId);
        if (!size) continue;

        // Check 4-connected neighbors
        const neighbors: [number, number][] = [
          [x + 1, y],
          [x - 1, y],
          [x, y + 1],
          [x, y - 1],
        ];

        let isBoundary = false;

        for (const [nx, ny] of neighbors) {
          if (nx < 0 || nx >= width || ny < 0 || ny >= height) {
            // Image edge counts as adjacency to empty space (region 0)
            isBoundary = true;
            const regionAdjacency = adjacency.get(regionId);
            if (regionAdjacency) {
              regionAdjacency.set(0, (regionAdjacency.get(0) || 0) + 1);
            }
            continue;
          }

          const nIdx = ny * width + nx;
          const neighborRegionId = componentId[nIdx];

          if (neighborRegionId !== regionId) {
            isBoundary = true;

            // Track adjacency to all neighbors including empty (regionId 0)
            const regionAdjacency = adjacency.get(regionId);
            if (regionAdjacency) {
              regionAdjacency.set(neighborRegionId, (regionAdjacency.get(neighborRegionId) || 0) + 1);
            }
          }
        }

        if (isBoundary) {
          perimeterCounts.set(regionId, (perimeterCounts.get(regionId) || 0) + 1);
        }
      }
    }

    // Directions for ray-casting to find interior point
    const dirs = [
      [1, 0],
      [-1, 0],
      [0, 1],
      [0, -1],
      [1, 1],
      [-1, 1],
      [1, -1],
      [-1, -1],
    ];

    // Second pass: build graph nodes for all regions
    for (const [regionId, size] of regionSizes) {
      const centroidSum = regionCentroids.get(regionId);
      if (!centroidSum) continue;

      const cx = centroidSum.sumX / size;
      const cy = centroidSum.sumY / size;

      // Compute interior point
      let ix = Math.round(cx);
      let iy = Math.round(cy);

      if (componentId[iy * width + ix] !== regionId) {
        ix = -1;
        iy = -1;
        const rawCx = Math.round(cx);
        const rawCy = Math.round(cy);
        let bestLen = -1;

        for (const [dx, dy] of dirs) {
          let enter = -1;
          let len = 0;
          for (let step = 0; step < Math.max(width, height); step++) {
            const nx = rawCx + dx * step;
            const ny = rawCy + dy * step;
            if (nx < 0 || nx >= width || ny < 0 || ny >= height) break;
            if (componentId[ny * width + nx] === regionId) {
              if (enter < 0) enter = step;
              len = step - enter + 1;
            } else if (enter >= 0) {
              break;
            }
          }
          if (len > bestLen) {
            bestLen = len;
            const mid = enter + Math.floor(len / 2);
            ix = rawCx + dx * mid;
            iy = rawCy + dy * mid;
          }
        }

        if (ix < 0) {
          const sampleIdx = regionSamplePixel[regionId];
          if (sampleIdx !== undefined) {
            ix = sampleIdx % width;
            iy = (sampleIdx - ix) / width;
          } else {
            ix = Math.round(cx);
            iy = Math.round(cy);
          }
        }
      }

      const perimeterPixels = perimeterCounts.get(regionId) || 1;
      const regionAdjacency = adjacency.get(regionId) || new Map();

      const edges: RegionGraphEdge[] = [];
      for (const [targetRegionId, sharedPixels] of regionAdjacency) {
        edges.push({
          targetRegionId,
          sharedPixels,
          connectivityPercent: sharedPixels / perimeterPixels,
        });
      }

      edges.sort((a, b) => b.connectivityPercent - a.connectivityPercent);

      nodes.set(regionId, {
        regionId,
        centroid: { x: cx, y: cy },
        interiorPoint: { x: ix, y: iy },
        centroidNormalized: { x: cx / width, y: cy / height },
        interiorPointNormalized: { x: ix / width, y: iy / height },
        size,
        band: regionBands.get(regionId) ?? 0,
        touchesEdge: edgeTouchingRegions.has(regionId),
        edges,
        perimeterPixels,
        selected: false,
      });
    }

    const minSize = kwargs?.regionGraphMinSize ?? 500;

    return {
      ...ctx,
      regionGraph: { nodes, minSize },
      showRegionGraph: kwargs?.showRegionGraph ?? false,
    };
  }

  /**
   * Fast pass to merge small regions into their best-connected neighbor.
   * Uses the region graph for O(regions) complexity instead of O(pixels).
   *
   * Algorithm:
   * 1. Collect all small regions (size <= maxPixels) sorted by size ascending
   * 2. For each small region, find neighbor with highest connectivity —
   *    optionally gated by `mergeSmallRegionsMaxHeightDelta`: neighbors whose
   *    mean MIRRORED gray (`ctx.gray`, the banding depth signal) differs more
   *    than the delta are skipped, so a sliver isn't absorbed by a touching
   *    surface in a different depth band. A region with no gate-passing
   *    neighbor stays unmerged.
   * 3. Merge into that neighbor: update componentId, regionSizes, and graph edges
   * 4. Remove merged region from graph
   *
   * Height means are computed from each region's ORIGINAL pixels (merge
   * targets are resolved before any mutation), so a target that absorbs
   * several slivers is judged by its own mass — an acceptable approximation
   * since the target is by definition the larger body.
   */
  private mergeSmallRegionsPass(ctx: DepthSegmentationContext, kwargs?: DepthSegmentationKwargs): DepthSegmentationContext {
    const { regionGraph, componentId, regionSizes, regionPixels } = ctx;
    if (!regionGraph || !componentId || !regionSizes || !regionPixels) return ctx;

    const maxPixels = kwargs?.mergeSmallRegionsMaxPixels ?? 100;
    const maxHeightDelta = kwargs?.mergeSmallRegionsMaxHeightDelta ?? 0;
    const nodes = regionGraph.nodes;

    // Lazily-computed mean height per region, for the optional height gate.
    // Reads the MIRRORED grayscale (`ctx.gray`, the same depth signal band
    // assignment uses) so the gate agrees with the banding; falls back to the
    // raw source B channel if the grayscale pass hasn't run. Sampled with a
    // stride so huge regions stay cheap. Unrendered pixels (alpha === 0 /
    // B === 0) are skipped; a region with no usable height sample returns
    // null and the gate treats it as "no information: allow".
    const { gray, alpha } = ctx;
    const meanHeights = new Map<number, number | null>();
    const meanHeightOf = (regionId: number): number | null => {
      const cached = meanHeights.get(regionId);
      if (cached !== undefined) return cached;
      const pixels = regionPixels.get(regionId);
      let mean: number | null = null;
      if (pixels && pixels.length > 0) {
        const MAX_SAMPLES = 4096;
        const stride = Math.max(1, Math.ceil(pixels.length / MAX_SAMPLES));
        let sum = 0;
        let count = 0;
        for (let i = 0; i < pixels.length; i += stride) {
          const p = pixels[i];
          if (alpha ? alpha[p] === 0 : ctx.src[p * 4 + 2] === 0) continue;
          sum += gray ? gray[p] : ctx.src[p * 4 + 2];
          count++;
        }
        if (count > 0) mean = sum / count;
      }
      meanHeights.set(regionId, mean);
      return mean;
    };
    const heightGateAllows = (smallId: number, targetId: number): boolean => {
      if (maxHeightDelta <= 0) return true;
      const a = meanHeightOf(smallId);
      const b = meanHeightOf(targetId);
      if (a === null || b === null) return true;
      return Math.abs(a - b) <= maxHeightDelta;
    };

    // Phase 1: Decide merge targets (no mutations yet)
    // Union-Find with path compression for fast chain resolution
    const parent = new Map<number, number>();
    const find = (id: number): number => {
      let root = id;
      while (parent.has(root)) root = parent.get(root) ?? root;
      // Path compression
      let cur = id;
      while (cur !== root) {
        const next = parent.get(cur) ?? root;
        parent.set(cur, root);
        cur = next;
      }
      return root;
    };

    // Collect small regions sorted by size (smallest first)
    const smallRegions: number[] = [];
    for (const [regionId, node] of nodes) {
      if (node.size <= maxPixels) {
        smallRegions.push(regionId);
      }
    }
    smallRegions.sort((a, b) => (nodes.get(a)?.size ?? 0) - (nodes.get(b)?.size ?? 0));

    for (const smallRegionId of smallRegions) {
      if (parent.has(smallRegionId)) continue;

      const node = nodes.get(smallRegionId);
      if (!node || node.edges.length === 0) continue;

      // Find best neighbor: highest shared boundary (connectivity), among
      // neighbors whose mean height is close enough (when the gate is on).
      let bestTargetId = -1;
      let bestSharedPixels = -1;

      for (const edge of node.edges) {
        const finalTarget = find(edge.targetRegionId);
        if (finalTarget === smallRegionId) continue;

        const targetNode = nodes.get(finalTarget);
        if (!targetNode) continue;
        if (!heightGateAllows(smallRegionId, finalTarget)) continue;

        if (edge.sharedPixels > bestSharedPixels) {
          bestSharedPixels = edge.sharedPixels;
          bestTargetId = finalTarget;
        }
      }

      if (bestTargetId < 0) continue;
      parent.set(smallRegionId, bestTargetId);
    }

    // Phase 2: Apply merges in a single pass over componentId
    // Build set of merged region IDs for fast lookup
    if (parent.size === 0) return ctx;

    const total = componentId.length;
    for (let i = 0; i < total; i++) {
      const cid = componentId[i];
      if (parent.has(cid)) {
        componentId[i] = find(cid);
      }
    }

    // Phase 3: Rebuild regionPixels, regionSizes from componentId in one scan
    const mergedIds = new Set<number>();
    for (const [id] of parent) {
      mergedIds.add(id);
    }

    // Update pixel lists: remove merged, rebuild targets
    // Clear target pixel lists that will receive merged pixels
    const affectedTargets = new Set<number>();
    for (const [id] of parent) {
      affectedTargets.add(find(id));
    }
    // Rebuild affected target pixel lists from scratch (faster than pushing)
    for (const targetId of affectedTargets) {
      regionPixels.set(targetId, []);
    }
    // Single scan to rebuild pixel lists for affected targets
    for (let i = 0; i < total; i++) {
      const cid = componentId[i];
      if (affectedTargets.has(cid)) {
        regionPixels.get(cid)?.push(i);
      }
    }

    // Update sizes and remove merged entries
    for (const targetId of affectedTargets) {
      const pixels = regionPixels.get(targetId);
      if (pixels) {
        regionSizes.set(targetId, pixels.length);
        const targetNode = nodes.get(targetId);
        if (targetNode) targetNode.size = pixels.length;
      }
    }
    for (const mergedId of mergedIds) {
      regionSizes.delete(mergedId);
      regionPixels.delete(mergedId);
    }

    // Fold merged regions' centroid sums (and edge-touching flags) into their
    // targets. Without this, a later `region_graph` re-run divides the STALE
    // sum by the merged (larger) size — dragging every merge target's node
    // toward the image origin while the repainted segmentation stays correct.
    const { regionCentroids, edgeTouchingRegions, width, height } = ctx;
    if (regionCentroids) {
      for (const mergedId of mergedIds) {
        const targetId = find(mergedId);
        const mergedSum = regionCentroids.get(mergedId);
        const targetSum = regionCentroids.get(targetId);
        if (mergedSum && targetSum) {
          targetSum.sumX += mergedSum.sumX;
          targetSum.sumY += mergedSum.sumY;
        }
        regionCentroids.delete(mergedId);
        if (edgeTouchingRegions?.delete(mergedId)) {
          edgeTouchingRegions.add(targetId);
        }
      }
      // Keep the in-place graph honest too (configs that don't re-run
      // region_graph after this pass): recompute the surviving targets'
      // centroids from the folded sums. Interior points are left as-is —
      // the target only grew, so its old interior point is still inside.
      for (const targetId of affectedTargets) {
        const sum = regionCentroids.get(targetId);
        const size = regionSizes.get(targetId);
        const node = nodes.get(targetId);
        if (!sum || !size || !node) continue;
        const cx = sum.sumX / size;
        const cy = sum.sumY / size;
        node.centroid = { x: cx, y: cy };
        node.centroidNormalized = { x: cx / width, y: cy / height };
      }
    }

    // Phase 4: Update coloredData to reflect merged regions
    const { coloredData, regionColors } = ctx;
    if (coloredData && regionColors) {
      // Remove old color entries for merged regions
      const colorsToRemove: number[] = [];
      for (const [colorKey, regId] of regionColors) {
        if (mergedIds.has(regId)) {
          colorsToRemove.push(colorKey);
        }
      }
      for (const colorKey of colorsToRemove) {
        regionColors.delete(colorKey);
      }

      // Repaint pixels that changed region
      for (let i = 0; i < total; i++) {
        const cid = componentId[i];
        if (cid === 0) continue;
        if (!affectedTargets.has(cid)) continue;

        const pi = i * 4;
        const r = cid & 0xff;
        const g = (cid >> 8) & 0xff;
        const b = (cid >> 16) & 0xff;
        coloredData[pi] = r;
        coloredData[pi + 1] = g;
        coloredData[pi + 2] = b;
        coloredData[pi + 3] = 255;

        const colorKey = r | (g << 8) | (b << 16);
        if (!regionColors.has(colorKey)) regionColors.set(colorKey, cid);
      }
    }

    // Phase 5: Rebuild graph edges for surviving nodes
    for (const mergedId of mergedIds) {
      nodes.delete(mergedId);
    }
    for (const [, node] of nodes) {
      // Remap edge targets and deduplicate
      const edgeMap = new Map<number, RegionGraphEdge>();
      for (const edge of node.edges) {
        const target = find(edge.targetRegionId);
        if (target === node.regionId) continue; // Remove self-edges
        if (mergedIds.has(target)) continue; // Target was also merged (shouldn't happen after find, but safety)
        const existing = edgeMap.get(target);
        if (existing) {
          existing.sharedPixels += edge.sharedPixels;
        } else {
          edgeMap.set(target, {
            targetRegionId: target,
            sharedPixels: edge.sharedPixels,
            connectivityPercent: 0,
          });
        }
      }
      node.edges = Array.from(edgeMap.values());
    }

    return ctx;
  }

  /**
   * Fill enclosed unassigned-pixel holes by assigning them to their dominant
   * neighbour region. "Enclosed" = the connected component of unassigned
   * pixels (componentId === 0) does NOT touch any image border, so anything
   * the user can see "behind" the captured frame is left alone.
   *
   * Runs on the same raw input as `fill_small_gaps` (a BFS over componentId
   * === 0 pixels), differing in two ways:
   *   - No size cap by default: any fully-enclosed hole is filled, regardless
   *     of how large. Pass a positive `fillEnclosedHolesMaxPixels` to cap.
   *   - Gaps touching the canvas border are explicitly skipped — those are
   *     the wide black margins around the wall view in top-down ortho and
   *     filling them would erase the frame entirely.
   *
   * `componentId`, `regionSizes`, and `regionCentroids` are updated in place.
   * The colored canvas / region graph passes downstream regenerate from
   * `componentId`, so they pick up the changes automatically.
   */
  private fillEnclosedHolesPass(ctx: DepthSegmentationContext, kwargs?: DepthSegmentationKwargs): DepthSegmentationContext {
    const { componentId, width, height, regionSizes, regionBands, regionCentroids } = ctx;
    if (!componentId || !regionSizes || !regionBands || !regionCentroids) return ctx;

    const maxPixels = kwargs?.fillEnclosedHolesMaxPixels ?? 0;
    const total = width * height;
    const visited = new Uint8Array(total);
    const queue: number[] = [];

    for (let startIdx = 0; startIdx < total; startIdx++) {
      if (componentId[startIdx] !== 0 || visited[startIdx]) continue;

      // BFS over connected unassigned pixels. Track whether any pixel in the
      // hole touches the image border — if so we skip the whole hole.
      const gapPixels: number[] = [];
      const neighborRegions = new Map<number, number>();
      let touchesEdge = false;

      queue.length = 0;
      queue.push(startIdx);
      visited[startIdx] = 1;

      while (queue.length > 0) {
        const idx = queue.shift() as number;
        gapPixels.push(idx);

        const x = idx % width;
        const y = Math.floor(idx / width);

        if (x === 0 || x === width - 1 || y === 0 || y === height - 1) {
          touchesEdge = true;
        }

        const neighbors: [number, number][] = [
          [x + 1, y],
          [x - 1, y],
          [x, y + 1],
          [x, y - 1],
        ];

        for (const [nx, ny] of neighbors) {
          if (nx < 0 || nx >= width || ny < 0 || ny >= height) continue;
          const nIdx = ny * width + nx;

          if (componentId[nIdx] === 0) {
            if (!visited[nIdx]) {
              visited[nIdx] = 1;
              queue.push(nIdx);
            }
          } else {
            const neighborRegionId = componentId[nIdx];
            neighborRegions.set(neighborRegionId, (neighborRegions.get(neighborRegionId) || 0) + 1);
          }
        }
      }

      if (touchesEdge) continue;
      if (maxPixels > 0 && gapPixels.length > maxPixels) continue;

      // Dominant neighbour by adjacent-pixel count wins.
      let bestRegionId = 0;
      let bestCount = 0;
      for (const [regionId, count] of neighborRegions) {
        if (count > bestCount) {
          bestCount = count;
          bestRegionId = regionId;
        }
      }
      if (bestRegionId === 0) continue;

      for (const idx of gapPixels) {
        componentId[idx] = bestRegionId;
      }

      regionSizes.set(bestRegionId, (regionSizes.get(bestRegionId) || 0) + gapPixels.length);
      const centroid = regionCentroids.get(bestRegionId);
      if (centroid) {
        for (const idx of gapPixels) {
          centroid.sumX += idx % width;
          centroid.sumY += Math.floor(idx / width);
        }
      }
    }

    return ctx;
  }

  /**
   * Generate auto-prompts with convex hulls for each region.
   */
  private autoPromptsPass(ctx: DepthSegmentationContext, kwargs?: DepthSegmentationKwargs): DepthSegmentationContext {
    const { regionGraph, regionPixels, regionBands, edgeTouchingRegions, width, height } = ctx;

    if (!regionGraph || !regionPixels || !regionBands || !edgeTouchingRegions) {
      return ctx;
    }

    const topBandPercent = kwargs?.topBandPercent ?? 0.5;

    // Find max band among non-edge regions
    let maxBand = 0;
    for (const node of regionGraph.nodes.values()) {
      if (!node.touchesEdge && node.band > maxBand) {
        maxBand = node.band;
      }
    }
    const bandThreshold = Math.floor(maxBand * (1 - topBandPercent));

    const autoPrompts: DepthAutoPrompt[] = [];

    // Get minimum region size from context (set by connected components pass)
    const minRegionPixels = ctx.minRegionPixels ?? 50;

    for (const node of regionGraph.nodes.values()) {
      // Skip regions that are too small
      if (node.size < minRegionPixels) continue;

      const label = !node.touchesEdge && node.band >= bandThreshold ? 1 : 0;

      const pixelList = regionPixels.get(node.regionId) ?? [];
      const hull = this.convexHullPixels(pixelList, width);

      autoPrompts.push({
        x: node.interiorPointNormalized.x,
        y: node.interiorPointNormalized.y,
        label,
        hull,
        imageWidth: width,
        imageHeight: height,
      });
    }

    return { ...ctx, autoPrompts };
  }

  /**
   * Compute convex hull of pixel indices using gift wrapping algorithm.
   */
  private convexHullPixels(pixelIndices: number[], width: number): Array<{ px: number; py: number }> {
    if (pixelIndices.length === 0) return [];

    const pts = pixelIndices.map((idx) => ({
      px: idx % width,
      py: Math.floor(idx / width),
    }));

    if (pts.length <= 3) return pts;

    // Find leftmost point
    let startIdx = 0;
    for (let i = 1; i < pts.length; i++) {
      if (pts[i].px < pts[startIdx].px || (pts[i].px === pts[startIdx].px && pts[i].py < pts[startIdx].py)) {
        startIdx = i;
      }
    }

    const hull: Array<{ px: number; py: number }> = [];
    let current = startIdx;

    do {
      hull.push(pts[current]);
      let next = 0;

      for (let i = 1; i < pts.length; i++) {
        if (i === current) continue;

        const ax = pts[next].px - pts[current].px;
        const ay = pts[next].py - pts[current].py;
        const bx = pts[i].px - pts[current].px;
        const by = pts[i].py - pts[current].py;
        const cross = ax * by - ay * bx;

        if (cross < 0) {
          next = i;
        } else if (cross === 0) {
          // Collinear — keep the farthest point
          const d1 = ax * ax + ay * ay;
          const d2 = bx * bx + by * by;
          if (d2 > d1) next = i;
        }
      }

      if (next === startIdx) break;
      current = next;
    } while (hull.length < pts.length);

    return hull;
  }
}
